import { useTranslation } from "react-i18next";
import { useState, useRef, useEffect } from "react";
import { BaseFillTheBlankQuestionDto } from "@packages/api/madlenAPI.schemas";
import { X, Plus, GripVertical } from "lucide-react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  DraggingStyle,
  NotDraggingStyle,
  DragStart,
} from "react-beautiful-dnd";
import { Button } from "../../../../../core";
import { Input } from "@core/input";
import { IoInformationCircleOutline } from "react-icons/io5";
import * as HoverCard from "@radix-ui/react-hover-card";

const getItemStyle = (
  isDragging: boolean,
  draggableStyle: DraggingStyle | NotDraggingStyle | undefined,
): React.CSSProperties => ({
  userSelect: "none",
  width: "100%",

  // change background colour if dragging
  background: isDragging ? "rgba(59, 130, 246, 0.1)" : undefined,
  boxShadow: isDragging ? "0 4px 6px -1px rgba(0, 0, 0, 0.1)" : undefined,
  zIndex: isDragging ? 50 : undefined,

  // styles we need to apply on draggables
  ...draggableStyle,

  // This ensures position is always calculated correctly
  transformOrigin: "0 0",
  margin: 0,
});

interface FillBlankQuestionEditorProps {
  question: BaseFillTheBlankQuestionDto & { possibleAnswers?: string[] };
  onQuestionChange: (
    question: BaseFillTheBlankQuestionDto & { possibleAnswers?: string[] },
  ) => void;
}

export const FillBlankQuestionEditor = ({
  question,
  onQuestionChange,
}: FillBlankQuestionEditorProps) => {
  const { t } = useTranslation();
  const [selection, setSelection] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const textareaContainerRef = useRef<HTMLDivElement>(null);
  const [selectionStart, setSelectionStart] = useState(0);
  const [selectionEnd, setSelectionEnd] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Function to convert {{BLANK}} to numbered blanks for display
  const getDisplayText = (text: string) => {
    let count = 1;
    return text.replace(/{{BLANK}}/g, () => `___${count++}___`);
  };

  // Function to convert numbered blanks back to {{BLANK}}
  const getStorageText = (text: string) => {
    return text.replace(/___\d+___/g, "{{BLANK}}");
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        setShowPopup(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleTextSelection = () => {
    const selection = window.getSelection();
    if (selection && selection.toString().trim() && textareaRef.current) {
      setSelection(selection.toString());
      setSelectionStart(textareaRef.current.selectionStart);
      setSelectionEnd(textareaRef.current.selectionEnd);
      setShowPopup(true);
    } else {
      setShowPopup(false);
    }
  };

  const addBlank = (text: string) => {
    const displayText = getDisplayText(question.body);
    const beforeSelection = displayText.slice(0, selectionStart);
    const afterSelection = displayText.slice(selectionEnd);
    const blankCount = (beforeSelection.match(/___\d+___/g) || []).length;
    const newDisplayText = `${beforeSelection}___${blankCount + 1}___${afterSelection}`;
    const newStorageText = getStorageText(newDisplayText);

    // Count number of BLANKs before the insertion point
    const blanksBefore = (beforeSelection.match(/___\d+___/g) || []).length;

    // Create new answers array with the new answer inserted at correct position
    const newAnswers = [...question.answer];
    newAnswers.splice(blanksBefore, 0, text);
    const newPossibleAnswers = [...(question.possibleAnswers || [])];
    newPossibleAnswers.splice(blanksBefore, 0, text);
    onQuestionChange({
      ...question,
      body: newStorageText,
      answer: newAnswers,
      possibleAnswers: newPossibleAnswers,
    });
    setShowPopup(false);
  };

  const handleDragStart = (start: DragStart) => {
    setIsDragging(true);
    console.log(`Started dragging item with ID: ${start.draggableId}`);
  };

  const handleDragEnd = (result: DropResult) => {
    // Reset dragging state
    setIsDragging(false);

    if (!result.destination) {
      return;
    }

    if (result.source.index === result.destination.index) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    // Create a copy of the answers array and reorder it
    const newAnswers = Array.from(question.answer);
    const [movedItem] = newAnswers.splice(sourceIndex, 1);
    newAnswers.splice(destinationIndex, 0, movedItem);

    // The trick is to keep the {{ BLANK }} tokens in the same position
    // but change the order of answers that correspond to each blank

    onQuestionChange({
      ...question,
      answer: newAnswers,
    });
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          {t("assignments.generator.preview.question")}
        </label>
        <div ref={textareaContainerRef} className="relative">
          <textarea
            ref={textareaRef}
            onMouseUp={handleTextSelection}
            onTouchEnd={handleTextSelection}
            value={getDisplayText(question.body)}
            onChange={(e) => {
              onQuestionChange({
                ...question,
                body: getStorageText(e.target.value),
              });
            }}
            className="w-full p-3 min-h-[100px] border rounded-md dark:bg-gray-600 dark:border-gray-500 dark:text-gray-100"
            placeholder={t("assignments.generator.preview.questionPlaceholder")}
          />
          {showPopup && (
            <div
              ref={popupRef}
              className="absolute left-0 right-0 mt-2 flex justify-center z-10 animate-fade-in"
            >
              <div className="bg-white dark:bg-gray-700 shadow-lg rounded-md p-2">
                <Button
                  onClick={() => addBlank(selection)}
                  size="sm"
                  rounded="md"
                  bgColor="hover:bg-blue-50 dark:hover:bg-blue-900/30"
                  textColor="text-blue-600 dark:text-blue-400"
                  className="flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {t("assignments.generator.preview.addBlank")}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
            {t("assignments.generator.preview.blanks")}
          </label>
          <HoverCard.Root openDelay={0} closeDelay={0}>
            <HoverCard.Trigger asChild>
              <button className="focus:outline-none">
                <IoInformationCircleOutline className="w-4 h-4 text-gray-600 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-400" />
              </button>
            </HoverCard.Trigger>
            <HoverCard.Portal>
              <HoverCard.Content
                className="bg-white dark:bg-gray-800 rounded-lg shadow-lg px-3 py-2 text-sm"
                side="top"
                align="center"
                sideOffset={5}
              >
                {t("assignments.generator.questions.fillBlankIntroduction")}
                <HoverCard.Arrow className="fill-white dark:fill-gray-800" />
              </HoverCard.Content>
            </HoverCard.Portal>
          </HoverCard.Root>
        </div>
        <DragDropContext
          onDragEnd={handleDragEnd}
          onDragStart={handleDragStart}
        >
          <Droppable droppableId="blanks" direction="vertical">
            {(provided, snapshot) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className={`space-y-2 ${
                  snapshot.isDraggingOver
                    ? "bg-gray-100 dark:bg-gray-800 p-2 rounded-md border-2 border-dashed border-blue-300 dark:border-blue-700"
                    : isDragging
                      ? "bg-gray-50 dark:bg-gray-900 p-2 rounded-md border-2 border-dashed border-red-300 dark:border-red-700"
                      : ""
                }`}
                style={{ position: "relative", minHeight: "40px" }}
              >
                {question.answer.map((answer, index) => (
                  <Draggable
                    key={`blank-${index}`}
                    draggableId={`blank-${index}`}
                    index={index}
                  >
                    {(provided, snapshot) => {
                      // https://github.com/atlassian/react-beautiful-dnd/issues/1881 found this solution
                      if (snapshot.isDragging) {
                        // @ts-ignore
                        provided.draggableProps.style = {
                          ...provided.draggableProps.style,
                          // @ts-ignore
                          left: provided.draggableProps.style?.offsetLeft,
                          // @ts-ignore
                          top: provided.draggableProps.style?.offsetTop,
                        };
                      }
                      // Use our helper function to get stable positioning
                      return (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700 ${
                            snapshot.isDragging ? "shadow-lg" : ""
                          } rounded-md group`}
                          style={getItemStyle(
                            snapshot.isDragging,
                            provided.draggableProps.style,
                          )}
                        >
                          <div
                            {...provided.dragHandleProps}
                            className="cursor-grab active:cursor-grabbing text-gray-400 p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md"
                          >
                            <GripVertical className="w-4 h-4" />
                          </div>
                          <span className="w-6 text-sm text-gray-500 dark:text-gray-400">
                            #{index + 1}
                          </span>
                          <Input
                            type="text"
                            value={answer}
                            onChange={(e) => {
                              const newAnswers = [...question.answer];
                              newAnswers[index] = e.target.value;
                              onQuestionChange({
                                ...question,
                                answer: newAnswers,
                              });
                            }}
                            classNames={{
                              input:
                                "flex-1 p-2 border rounded-md dark:bg-gray-600 dark:border-gray-500 dark:text-gray-100",
                            }}
                          />
                          <Button
                            onClick={() => {
                              const newAnswers = [...question.answer];
                              newAnswers.splice(index, 1);

                              // Split text by {{BLANK}} to get array of text segments
                              const segments = question.body.split("{{BLANK}}");

                              // Reconstruct body by joining segments with {{BLANK}}, skipping the index we want to remove
                              const newBody = segments.reduce(
                                (acc, segment, i) => {
                                  if (i === segments.length - 1)
                                    return acc + segment;
                                  if (i === index)
                                    return acc + segment + question.answer[i];
                                  return acc + segment + "{{BLANK}}";
                                },
                                "",
                              );

                              onQuestionChange({
                                ...question,
                                body: newBody,
                                answer: newAnswers,
                              });
                            }}
                            textColor="text-gray-400 hover:text-red-500"
                            size="icon"
                            className="opacity-0 group-hover:opacity-100"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    }}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      {/* Possible Answers Section */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
            {t("assignments.generator.preview.options")}
          </label>
          <HoverCard.Root openDelay={0} closeDelay={0}>
            <HoverCard.Trigger asChild>
              <button className="focus:outline-none">
                <IoInformationCircleOutline className="w-4 h-4 text-gray-600 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-400" />
              </button>
            </HoverCard.Trigger>
            <HoverCard.Portal>
              <HoverCard.Content
                className="bg-white dark:bg-gray-800 rounded-lg shadow-lg px-3 py-2 text-sm"
                side="top"
                align="center"
                sideOffset={5}
              >
                {t(
                  "assignments.generator.questions.possibleAnswersIntroduction",
                )}
                <HoverCard.Arrow className="fill-white dark:fill-gray-800" />
              </HoverCard.Content>
            </HoverCard.Portal>
          </HoverCard.Root>
        </div>

        <div className="space-y-2">
          {(question.possibleAnswers || []).map((answer, index) => (
            <div
              key={`possible-answer-${index}`}
              className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-md group"
            >
              <span className="w-6 text-sm text-gray-500 dark:text-gray-400">
                #{index + 1}
              </span>
              <Input
                type="text"
                value={answer}
                onChange={(e) => {
                  const newPossibleAnswers = [
                    ...(question.possibleAnswers || []),
                  ];
                  newPossibleAnswers[index] = e.target.value;
                  onQuestionChange({
                    ...question,
                    possibleAnswers: newPossibleAnswers,
                  });
                }}
                classNames={{
                  input:
                    "flex-1 p-2 border rounded-md dark:bg-gray-600 dark:border-gray-500 dark:text-gray-100",
                }}
              />
              <Button
                onClick={() => {
                  const newPossibleAnswers = [
                    ...(question.possibleAnswers || []),
                  ];
                  newPossibleAnswers.splice(index, 1);
                  onQuestionChange({
                    ...question,
                    possibleAnswers: newPossibleAnswers,
                  });
                }}
                textColor="text-gray-400 hover:text-red-500"
                size="icon"
                className="opacity-0 group-hover:opacity-100"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
        <Button
          onClick={() => {
            onQuestionChange({
              ...question,
              possibleAnswers: [...(question.possibleAnswers || []), ""],
            });
          }}
          size="sm"
          rounded="md"
          bgColor="hover:bg-blue-50 dark:hover:bg-blue-900/30"
          textColor="text-blue-600 dark:text-blue-400"
          className="flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          {t("assignments.generator.preview.addOption")}
        </Button>
      </div>
    </div>
  );
};
